import type { ComponentProps, ReactNode } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { GlowCard } from "@/ui/components/glow-card";
import { StatusDot, type Tone } from "@/ui/components/status-badge";
import { cn } from "@/lib/utils";

import { rowHover } from "./row-classes";

export function RowCard({ className, ...props }: ComponentProps<"section">) {
  return (
    <GlowCard
      className={cn(
        "grid min-w-0 shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 p-4 pr-4 ring-inset md:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.4fr)_auto] md:gap-x-5 md:pr-5",
        className,
      )}
      {...props}
    />
  );
}

const DOT: Record<Tone, string> = {
  positive: "bg-positive",
  warning: "bg-warning",
  destructive: "bg-destructive",
  neutral: "bg-foreground",
  faint: "bg-muted-foreground",
};

export function RowBubble({ icon, tone, small }: { icon: IconSvgElement; tone?: Tone; small?: boolean }) {
  return (
    <span className="pointer-events-none relative flex items-center">
      <span
        className={cn(
          "grid place-items-center rounded-full bg-foreground/[0.07] ring-[3px] ring-elevated",
          small ? "size-10" : "size-12",
        )}
      >
        <HugeiconsIcon icon={icon} strokeWidth={1.8} className="size-5 text-muted-foreground" />
      </span>
      {tone ? (
        <span className={cn("absolute right-0 bottom-0 size-3 rounded-full ring-[3px] ring-elevated", DOT[tone])} />
      ) : null}
    </span>
  );
}

export function RowChips({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("pointer-events-none col-span-3 min-w-0 md:col-span-1", className)}>
      <div className="flex min-w-0 flex-wrap items-center gap-2 md:justify-center">{children}</div>
    </div>
  );
}

export function MutedChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-foreground/[0.04] px-3 text-xs text-muted-foreground tabular-nums">
      {children}
    </span>
  );
}

export function RowStatus({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span
      className={cn(
        "pointer-events-none inline-flex items-center gap-1.5 text-[13px]",
        tone === "positive" && "text-positive",
        tone === "destructive" && "text-destructive",
        tone === "warning" && "text-warning",
        (tone === "faint" || tone === "neutral") && "text-muted-foreground",
      )}
    >
      <StatusDot tone={tone} />
      {label}
    </span>
  );
}

export function RowSide({ status, menu }: { status: ReactNode; menu: ReactNode }) {
  return (
    <div className="flex flex-col items-end gap-2 max-md:col-start-3 max-md:row-start-1">
      {status}
      {menu}
    </div>
  );
}

export function RowTitle({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <div className="pointer-events-none min-w-0">
      <p className="truncate text-[17px] leading-snug font-medium tracking-tight">{title}</p>
      {sub ? <p className="mt-0.5 truncate text-[13px] text-muted-foreground tabular-nums">{sub}</p> : null}
    </div>
  );
}

export function RowHit({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="absolute inset-0 rounded-[28px] focus-visible:outline-2 focus-visible:outline-ring"
    />
  );
}

/** Dense list row used inside a `GlowCard p-2` list. */
export function DenseRow({
  className,
  onClick,
  label,
  children,
}: {
  className?: string;
  onClick?: () => void;
  label?: string;
  children: ReactNode;
}) {
  const cls = cn("flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-start text-[13px]", rowHover, className);
  return (
    <li>
      {onClick ? (
        <button type="button" aria-label={label} onClick={onClick} className={cn(cls, "focus-visible:outline-2 focus-visible:outline-ring")}>
          {children}
        </button>
      ) : (
        <div className={cls}>{children}</div>
      )}
    </li>
  );
}
